'use client';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { dismissFreestarStickyFooter, isFreestarStickyFooterDismissed, queryFreestarStickyFooterElement } from '@benzinga/ads-utils';

const FREESTAR_NATIVE_CLOSE_SELECTOR = '.fs-close-button';

export const FreestarStickyFooterDismiss: React.FC<{ hidden?: boolean }> = ({ hidden }) => {
  const [footerElement, setFooterElement] = useState<HTMLElement | null>(null);
  const [hasNativeClose, setHasNativeClose] = useState(false);

  useEffect(() => {
    if (hidden) {
      setFooterElement(null);
      setHasNativeClose(false);
      return;
    }

    const persistNativeClose = (event: Event) => {
      const eventTarget = event.target;
      if (!(eventTarget instanceof Element)) return;
      if (!eventTarget.closest(FREESTAR_NATIVE_CLOSE_SELECTOR)) return;
      dismissFreestarStickyFooter();
      setFooterElement(null);
    };

    const syncFooter = () => {
      if (isFreestarStickyFooterDismissed()) {
        setFooterElement(null);
        setHasNativeClose(false);
        return;
      }
      const stickyFooter = queryFreestarStickyFooterElement();
      if (!stickyFooter) {
        setFooterElement(null);
        setHasNativeClose(false);
        return;
      }
      const isVisible =
        getComputedStyle(stickyFooter).display !== 'none' && stickyFooter.getBoundingClientRect().height > 0;
      if (!isVisible) {
        setFooterElement(null);
        setHasNativeClose(false);
        return;
      }
      setHasNativeClose(!!stickyFooter.querySelector(FREESTAR_NATIVE_CLOSE_SELECTOR));
      setFooterElement(stickyFooter);
    };

    syncFooter();
    document.body.addEventListener('click', persistNativeClose);
    const observer = new MutationObserver(syncFooter);
    observer.observe(document.body, { childList: true, subtree: true, attributeFilter: ['style', 'class'] });
    return () => {
      observer.disconnect();
      document.body.removeEventListener('click', persistNativeClose);
    };
  }, [hidden]);

  if (hidden || !footerElement || hasNativeClose) return null;

  return createPortal(
    <button
      aria-label="Close advertisement"
      className="absolute right-1 top-1 z-[4] flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-[11px] leading-none text-white"
      onClick={() => {
        dismissFreestarStickyFooter();
        setFooterElement(null);
      }}
      type="button"
    >
      ×
    </button>,
    footerElement,
  );
};
